import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { performanceService } from '../../services/performanceService';
import { useSnackbar } from '../../utils/useSnackbar';
import PageHeader from '../ui/PageHeader';
import { REVIEW_STATUS } from '../../constants/reviewType';

const QuarterlyReviewForm = () => {
  const { trackerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: userData } = useSelector((state) => state.userDetails);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // The review record from the previous page's DataGrid
  const reviewData = location.state?.review;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [template, setTemplate] = useState([]);
  
  // existing answers from backend
  const [employeeData, setEmployeeData] = useState([]);
  const [managerData, setManagerData] = useState([]);

  // Form state
  // We'll store answers as a nested object: answers[category][criteriaNo] = comment
  const [employeeAnswers, setEmployeeAnswers] = useState({});
  const [managerAnswers, setManagerAnswers] = useState({});

  useEffect(() => {
    if (!trackerId) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        // 1. Fetch template
        const tpl = await performanceService.getFormTemplate();
        setTemplate(tpl);

        // 2. Fetch existing data
        const dataStr = await performanceService.getReviewFormData(trackerId);
        if (dataStr) {
          const parsed = typeof dataStr === 'string' ? JSON.parse(dataStr) : dataStr;
          
          const empArr = typeof parsed.employee === 'string' ? JSON.parse(parsed.employee) : (parsed.employee || []);
          const mgrArr = typeof parsed.manager === 'string' ? JSON.parse(parsed.manager) : (parsed.manager || []);
          
          setEmployeeData(empArr);
          setManagerData(mgrArr);

          // Populate form state from arrays
          const empMap = {};
          empArr.forEach((ans) => {
            if (!empMap[ans.category]) empMap[ans.category] = {};
            empMap[ans.category][ans.criteriaNo] = ans.comment;
          });
          setEmployeeAnswers(empMap);

          const mgrMap = {};
          mgrArr.forEach((ans) => {
            if (!mgrMap[ans.category]) mgrMap[ans.category] = {};
            mgrMap[ans.category][ans.criteriaNo] = ans.comment;
          });
          setManagerAnswers(mgrMap);
        }
      } catch (err) {
        showSnackbar('Failed to load form data.', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackerId]);

  // Determine user role and mode
  // If the user's ID matches the review's employee ID, they are the employee.
  // Otherwise, if they have manager roles, they are the manager.
  const isEmployee = reviewData ? userData?.id === reviewData.userId : false;
  
  // A review is "PENDING" when it's the employee's turn.
  // A review is "EMPLOYEE_SUBMITTED" when it's the manager's turn.
  const isEmployeeTurn = reviewData?.status === REVIEW_STATUS.PENDING;
  const isManagerTurn = reviewData?.status === REVIEW_STATUS.EMPLOYEE_SUBMITTED;

  // Group template by category
  const groupedTemplate = useMemo(() => {
    const groups = {};
    template.forEach((item) => {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    });
    return groups;
  }, [template]);

  const handleEmployeeChange = (category, no, val) => {
    setEmployeeAnswers((prev) => ({
      ...prev,
      [category]: { ...(prev[category] || {}), [no]: val },
    }));
  };

  const handleManagerChange = (category, no, val) => {
    setManagerAnswers((prev) => ({
      ...prev,
      [category]: { ...(prev[category] || {}), [no]: val },
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const payload = {
        trackerId: Number(trackerId),
        userId: userData?.id,
        answers: [],
      };

      if (isEmployee && (isEmployeeTurn || isManagerTurn)) {
        // Collect employee answers
        Object.keys(employeeAnswers).forEach((cat) => {
          Object.keys(employeeAnswers[cat]).forEach((cNo) => {
            payload.answers.push({
              category: cat,
              criteriaNo: Number(cNo),
              comment: employeeAnswers[cat][cNo],
            });
          });
        });
        await performanceService.submitEmployeeComments(payload);
        showSnackbar('Your review has been submitted successfully!', 'success');
        setTimeout(() => navigate('/home/self-evaluation'), 1500);
      } 
      else if (!isEmployee && isManagerTurn) {
        payload.reviewedByName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim();
        // Collect manager answers
        Object.keys(managerAnswers).forEach((cat) => {
          Object.keys(managerAnswers[cat]).forEach((cNo) => {
            payload.answers.push({
              category: cat,
              criteriaNo: Number(cNo),
              comment: managerAnswers[cat][cNo],
            });
          });
        });
        await performanceService.submitManagerComments(payload);
        showSnackbar('Manager review completed successfully!', 'success');
        setTimeout(() => navigate('/home/employee-monitoring'), 1500);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || 'Failed to submit form.';
      showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!reviewData) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Review data not found. Please navigate from the reviews table.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <div className="leave-page-container">
      <PageHeader
        title="Quarterly Performance Review"
        subtitle={`Employee: ${reviewData.userName || '—'} | Due: ${new Date(reviewData.triggerDate).toLocaleDateString()}`}
        left={
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
        }
      />
      {SnackbarComponent}

      <Box sx={{ pb: 6 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : (
          Object.keys(groupedTemplate).map((category, idx) => (
            <Accordion key={idx} className="accordion" sx={{ borderRadius: '1rem !important', mb: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #edf2f7', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'rgba(4,88,100,0.04)', px: 3, py: 1, borderBottom: '1px solid #edf2f7' }}>
                <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                  {idx + 1}. {category}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {groupedTemplate[category].map((criteria, cIdx) => {
                  const empVal = employeeAnswers[category]?.[criteria.no] || '';
                  const mgrVal = managerAnswers[category]?.[criteria.no] || '';

                  // Employee can edit if it's their turn (PENDING) OR if it's already submitted (EMPLOYEE_SUBMITTED) but not completed.
                  const empEditable = isEmployee && (isEmployeeTurn || isManagerTurn);
                  // Manager can edit if it's their turn (and they are not the employee being reviewed).
                  const mgrEditable = !isEmployee && isManagerTurn;

                  return (
                    <Box key={cIdx} sx={{ p: { xs: 2, md: 3 }, borderBottom: cIdx < groupedTemplate[category].length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      {/* Highlighted Question */}
                      <Box sx={{ mb: 2.5, p: 1.5, bgcolor: 'rgba(4,88,100,0.03)', borderLeft: '3px solid', borderColor: 'primary.main', borderRadius: '0 6px 6px 0' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.5 }}>
                          {criteria.no}. {criteria.criteriaText}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: { xs: 0, sm: 2 } }}>
                        {/* Employee Answer Section */}
                        {category !== "Manager's Comments/Recommendations" && (
                          empEditable ? (
                            <TextField
                              label="Employee Comments"
                              multiline
                              minRows={1}
                              maxRows={4}
                              fullWidth
                              size="small"
                              value={empVal}
                              onChange={(e) => handleEmployeeChange(category, criteria.no, e.target.value)}
                              sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                              placeholder="Type your answer here..."
                            />
                          ) : (
                            <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px dashed #cbd5e1' }}>
                              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mb: 0.5, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                Employee Comments
                              </Typography>
                              <Typography variant="body2" sx={{ color: empVal ? '#1e293b' : '#94a3b8', whiteSpace: 'pre-wrap' }}>
                                {empVal || 'No answer provided.'}
                              </Typography>
                            </Box>
                          )
                        )}

                        {/* Manager Answer Section (Only show if not PENDING, or if we are a manager) */}
                        {(!isEmployeeTurn || !isEmployee) && (
                          mgrEditable ? (
                            <TextField
                              label="Manager Comments / Evaluation"
                              multiline
                              minRows={1}
                              maxRows={4}
                              fullWidth
                              size="small"
                              value={mgrVal}
                              onChange={(e) => handleManagerChange(category, criteria.no, e.target.value)}
                              sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                              placeholder="Type your evaluation here..."
                            />
                          ) : (
                            <Box sx={{ p: 1.5, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px dashed #bbf7d0' }}>
                              <Typography variant="caption" sx={{ color: '#166534', fontWeight: 700, display: 'block', mb: 0.5, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                Manager Comments / Evaluation
                              </Typography>
                              <Typography variant="body2" sx={{ color: mgrVal ? '#14532d' : '#86efac', whiteSpace: 'pre-wrap' }}>
                                {mgrVal || 'No evaluation provided.'}
                              </Typography>
                            </Box>
                          )
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          ))
        )}

        {/* Submit Button */}
        {!loading && ((isEmployee && (isEmployeeTurn || isManagerTurn)) || (!isEmployee && isManagerTurn)) && (
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={<SaveOutlinedIcon />}
              sx={{ px: 4, py: 1.2, fontWeight: 600 }}
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default QuarterlyReviewForm;
